import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Cloud,
  Layers,
  GitBranch,
  Boxes,
  ShieldCheck,
  Database,
  Shuffle,
  Code,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";

const Skills = () => {
  const { t } = useLanguage();
  
  // Category names come from the locale so this section changes language with the rest
  // of the site; previously they were hardcoded English.
  const c = t.skills.categories;

  const skillCategories = [
    {
      category: c.cloud,
      icon: Cloud,
      items: [
        { name: "AWS", level: "Advanced" },
        { name: "Amazon EKS", level: "Intermediate" },
        { name: "Google Cloud", level: "Intermediate" },
        { name: "Nutanix", level: "Basic" },
      ],
    },
    {
      category: c.iac,
      icon: Layers,
      items: [
        { name: "Terraform", level: "Advanced" },
        { name: "Ansible", level: "Advanced" },
        { name: "Atlantis (GitOps)", level: "Intermediate" },
        { name: "Packer", level: "Intermediate" },
        { name: "Molecule", level: "Intermediate" },
      ],
    },
    {
      category: c.cicd,
      icon: GitBranch,
      items: [
        { name: "Bitbucket Pipelines", level: "Advanced" },
        { name: "GitHub Actions", level: "Intermediate" },
        { name: "GitLab CI", level: "Intermediate" },
        { name: "Jenkins", level: "Intermediate" },
        { name: "Xcode Cloud", level: "Intermediate" },
      ],
    },
    {
      category: c.containers,
      icon: Boxes,
      items: [
        { name: "Docker", level: "Advanced" },
        { name: "Kubernetes", level: "Intermediate" },
        { name: "Helm", level: "Intermediate" },
        { name: "Prometheus", level: "Intermediate" },
        { name: "Grafana", level: "Intermediate" },
      ],
    },
    {
      category: c.security,
      icon: ShieldCheck,
      items: [
        { name: "Linux hardening", level: "Advanced" },
        { name: "IAM least privilege", level: "Advanced" },
        { name: "Nginx / TLS", level: "Intermediate" },
        { name: "OpenVPN", level: "Intermediate" },
        { name: "tfsec / Checkov", level: "Intermediate" },
      ],
    },
    {
      category: c.databases,
      icon: Database,
      items: [
        { name: "PostgreSQL", level: "Advanced" },
        { name: "Amazon Aurora", level: "Advanced" },
        { name: "Snowflake", level: "Intermediate" },
        { name: "Oracle", level: "Intermediate" },
        { name: "MongoDB", level: "Intermediate" },
      ],
    },
    {
      category: c.etl,
      icon: Shuffle,
      items: [
        { name: "Talend", level: "Advanced" },
        { name: "Pentaho", level: "Advanced" },
        { name: "AWS DMS / SCT", level: "Advanced" },
        { name: "Dataiku", level: "Intermediate" },
        { name: "Denodo", level: "Intermediate" },
      ],
    },
    {
      category: c.programming,
      icon: Code,
      items: [
        { name: "SQL", level: "Advanced" },
        { name: "Python", level: "Advanced" },
        { name: "Bash", level: "Advanced" },
        { name: "Go", level: "Intermediate" },
        { name: "JavaScript", level: "Intermediate" },
      ],
    },
    {
      category: c.soft,
      icon: Sparkles,
      items: [
        { name: "Leadership", level: "Team lead, mentoring" },
        { name: "Communication", level: "Stakeholder-facing" },
        { name: "Problem Solving", level: "Analytical" },
        { name: "English", level: "Fluent" },
        { name: "Indonesian", level: "Native" },
      ],
    },
  ];

  const getLevelColor = (level: string) => {
    if (level === "Advanced") return "bg-primary text-primary-foreground";
    if (level === "Intermediate") return "bg-accent text-accent-foreground";
    return "bg-secondary text-secondary-foreground";
  };

  return (
    <section id="skills" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t.skills.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {t.skills.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-6xl mx-auto">
          {skillCategories.map((category, index) => {
            const Icon = category.icon;
            return (
              <Card
                key={index}
                className="shadow-soft hover:shadow-medium transition-all duration-300 animate-fade-up hover-glow scroll-reveal-scale"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader className="pb-3 sm:pb-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10 flex-shrink-0">
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    </div>
                    <CardTitle className="text-base sm:text-lg">{category.category}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 sm:space-y-3">
                    {category.items.map((skill, sIndex) => (
                      <div key={sIndex} className="flex items-center justify-between gap-2">
                        <span className="text-xs sm:text-sm font-medium text-foreground">{skill.name}</span>
                        <Badge variant="outline" className={`text-[10px] sm:text-xs ${getLevelColor(skill.level)} flex-shrink-0`}>
                          {skill.level}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Skills;
